import { useT, itemName, rarityLabel } from '@/i18n';
import { getItem } from '@/domain/items';
import { upgradedStats } from '@/domain/upgrade';
import { useGameStore } from '@/state/gameStore';
import type { EquipmentStats, Rarity } from '@/domain/types';
import type { Listing } from '@/state/marketStore';
import { ItemIcon } from './ItemIcon';
import './ItemDetailModal.css';

interface ListingDetailModalProps {
  listing: Listing;
  /** "buy" = browse view (with Buy button) — "manage" = own listing (Cancel button) */
  variant: 'buy' | 'manage';
  /** Disabled reason for buy button (e.g. "own listing", "insufficient credits") */
  disabledReason?: string;
  busy?: boolean;
  onAction?: () => void;
  onClose: () => void;
}

const RARITY_COLOR: Record<Rarity, string> = {
  common: '#a4b8c8', uncommon: '#4affb0', rare: '#4aa7ff',
  epic: '#c64aff', legendary: '#ff9a2b', mythic: '#ff4af0',
};

function diffClass(d: number): string {
  if (d > 0) return 'item-diff--up';
  if (d < 0) return 'item-diff--down';
  return 'item-diff--same';
}

function diffSign(d: number): string {
  if (d > 0) return `+${d}`;
  if (d < 0) return `${d}`;
  return '±0';
}

function emptyStats(): EquipmentStats {
  return { attack: 0, defense: 0, health: 0 };
}

export function ListingDetailModal({
  listing,
  variant,
  disabledReason,
  busy,
  onAction,
  onClose,
}: ListingDetailModalProps) {
  const player = useGameStore((s) => s.player);
  const t = useT();
  const def = getItem(listing.itemId);
  if (!def || !player) return null;

  const color = RARITY_COLOR[listing.rarity];
  const newStats = def.stats ? upgradedStats(def.stats, listing.upgradeLevel) : emptyStats();

  // Compare with currently equipped in same slot
  const slot = def.equipSlot;
  const currentEquipped = slot ? player.equipment[slot] : undefined;
  const currentDef = currentEquipped ? getItem(currentEquipped.itemId) : null;
  const currentStats =
    currentEquipped && currentDef?.stats
      ? upgradedStats(currentDef.stats, currentEquipped.upgradeLevel)
      : emptyStats();

  const diff = {
    attack: newStats.attack - currentStats.attack,
    defense: newStats.defense - currentStats.defense,
    health: newStats.health - currentStats.health,
  };

  const cantAfford = variant === 'buy' && player.credits < listing.price;
  const showCompare = !!def.stats && slot;

  return (
    <div className="item-detail__overlay" onClick={onClose}>
      <div className="item-detail" onClick={(e) => e.stopPropagation()}>
        <div className="item-detail__header" style={{ borderColor: color }}>
          <ItemIcon itemId={def.id} sizeRem={3.2} />
          <div className="item-detail__heading">
            <div className="item-detail__name">
              {itemName(def.nameKey)}
              <span className="item-detail__level" style={{ color }}>+{listing.upgradeLevel}</span>
            </div>
            <div className="item-detail__rarity" style={{ color }}>
              {rarityLabel(listing.rarity)} · {slot ? t.character.slots[slot] : '—'}
            </div>
            <div className="item-detail__value" style={{ fontSize: '0.85rem', fontWeight: 700 }}>
              ◈ {listing.price.toLocaleString('de-DE')}
            </div>
            {variant === 'buy' && (
              <div style={{ fontSize: '0.65rem', color: 'var(--color-text-dim)', marginTop: '0.15rem' }}>
                Verkäufer: <strong style={{ color: 'var(--color-text)' }}>{listing.sellerName}</strong>
              </div>
            )}
          </div>
        </div>

        {/* Absolute stats */}
        {def.stats && (
          <div className="item-detail__stats">
            <div className="item-detail__stats-title">Stats</div>
            <div className="stat-row">
              <span>⚔ {t.character.attack}</span>
              <span className="stat-row__val">{newStats.attack}</span>
            </div>
            <div className="stat-row">
              <span>🛡 {t.character.defense}</span>
              <span className="stat-row__val">{newStats.defense}</span>
            </div>
            <div className="stat-row">
              <span>♥ {t.character.hp}</span>
              <span className="stat-row__val">{newStats.health}</span>
            </div>
          </div>
        )}

        {/* Comparison with currently equipped */}
        {showCompare && (
          <div className="item-detail__compare">
            <div className="item-detail__compare-title">
              {currentEquipped && currentDef
                ? `Vergleich zu: ${itemName(currentDef.nameKey)} +${currentEquipped.upgradeLevel}`
                : `Slot aktuell leer — Differenz beim Anlegen:`}
            </div>
            <div className={`compare-row ${diffClass(diff.attack)}`}>
              <span>⚔ ATK</span>
              <span className="compare-row__vals">
                {currentStats.attack} <span className="arrow">→</span> {newStats.attack}
              </span>
              <span className="compare-row__delta">{diffSign(diff.attack)}</span>
            </div>
            <div className={`compare-row ${diffClass(diff.defense)}`}>
              <span>🛡 DEF</span>
              <span className="compare-row__vals">
                {currentStats.defense} <span className="arrow">→</span> {newStats.defense}
              </span>
              <span className="compare-row__delta">{diffSign(diff.defense)}</span>
            </div>
            <div className={`compare-row ${diffClass(diff.health)}`}>
              <span>♥ HP</span>
              <span className="compare-row__vals">
                {currentStats.health} <span className="arrow">→</span> {newStats.health}
              </span>
              <span className="compare-row__delta">{diffSign(diff.health)}</span>
            </div>
          </div>
        )}

        <div className="item-detail__actions">
          {variant === 'buy' && (
            <button
              className="btn btn--primary"
              onClick={onAction}
              disabled={busy || cantAfford || !!disabledReason}
              title={disabledReason}
            >
              {busy ? '...' : `Kaufen — ◈ ${listing.price.toLocaleString('de-DE')}`}
            </button>
          )}
          {variant === 'manage' && listing.status === 'open' && (
            <button className="btn btn--danger" onClick={onAction} disabled={busy}>
              {busy ? '...' : 'Listing abbrechen'}
            </button>
          )}
          <button className="btn btn--ghost" onClick={onClose}>
            {t.upgrade.cancel}
          </button>
        </div>

        {variant === 'buy' && cantAfford && (
          <div style={{
            fontSize: '0.7rem', color: '#ff6b6b', textAlign: 'center',
            padding: '0.3rem', letterSpacing: '0.05em',
          }}>
            ⛔ Nicht genug Credits (Du: ◈ {player.credits.toLocaleString('de-DE')})
          </div>
        )}
        {variant === 'buy' && disabledReason && !cantAfford && (
          <div style={{
            fontSize: '0.7rem', color: 'var(--color-text-dim)', textAlign: 'center',
            padding: '0.3rem', letterSpacing: '0.05em',
          }}>
            {disabledReason}
          </div>
        )}
      </div>
    </div>
  );
}
